import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaUpload, FaTrash, FaFileAlt, FaSpinner, FaFilePdf, FaShieldAlt, FaUser, FaUsers, FaUserMd, FaStethoscope } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { adminAPI } from '../services/adminAPI';

const PLAN_TYPES = [
  { value: 'CORE', label: 'Core Program' },
  { value: 'MEDICAL', label: 'Medical Program' },
  { value: 'GLP1', label: 'GLP-1 Program' },
];

const DOCUMENT_TYPES = [
  { value: 'MEAL_PLAN', label: 'Meal Plan' },
  { value: 'WORKOUT_PLAN', label: 'Workout Plan' },
];

const AdminDashboard = () => {
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Upload form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [planType, setPlanType] = useState('CORE');
  const [documentType, setDocumentType] = useState('MEAL_PLAN');
  const [selectedUserId, setSelectedUserId] = useState(''); // empty = generic, number = personalized
  const [selectedClient, setSelectedClient] = useState(null); // store full client object
  const [uploadMode, setUploadMode] = useState('generic'); // 'generic' or 'personalized'

  useEffect(() => {
    fetchDocuments();
    fetchClients();
  }, []);

  const fetchDocuments = async () => {
    try {
      const data = await adminAPI.getAllDocuments();
      setDocuments(data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const data = await adminAPI.getActiveClients();
      setClients(data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleClientSelect = (userId) => {
    setSelectedUserId(userId);
    if (userId) {
      const client = clients.find(c => c.id.toString() === userId);
      if (client) {
        setSelectedClient(client);
        setPlanType(client.planType); // Auto-fill plan type from client's subscription
      }
    } else {
      setSelectedClient(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please select a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    if (uploadMode === 'personalized' && !selectedUserId) {
      toast.error('Please select a client for personalized plan');
      return;
    }

    setUploading(true);
    try {
      const userId = uploadMode === 'personalized' ? selectedUserId : null;
      const newDocument = await adminAPI.uploadDocument(selectedFile, planType, documentType, userId);
      setDocuments([newDocument, ...documents]);
      setSelectedFile(null);
      setSelectedUserId('');
      setSelectedClient(null);
      // Reset file input
      document.getElementById('file-input').value = '';
      toast.success(uploadMode === 'personalized'
        ? `Plan uploaded for ${selectedClient?.firstName} ${selectedClient?.lastName}`
        : 'Document uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    setDeleting(documentId);
    try {
      await adminAPI.deleteDocument(documentId);
      setDocuments(documents.filter((doc) => doc.id !== documentId));
      toast.success('Document deleted');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getPlanTypeLabel = (type) => {
    return PLAN_TYPES.find((p) => p.value === type)?.label || type;
  };

  const getDocumentTypeLabel = (type) => {
    return DOCUMENT_TYPES.find((d) => d.value === type)?.label || type;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <FaShieldAlt className="text-2xl text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-500">Manage coaches, clients, and documents</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 bg-white rounded-xl p-2 shadow-lg">
          <Link
            to="/admin"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-primary-600 text-white"
          >
            <FaFileAlt />
            Documents
          </Link>
          <Link
            to="/admin/coaches"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaUserMd />
            Coaches
          </Link>
          <Link
            to="/admin/clients"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaUsers />
            Clients
          </Link>
          <Link
            to="/admin/doctors"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaStethoscope />
            Doctors
          </Link>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaUpload className="text-primary-600" />
            Upload Plan Document
          </h2>

          <form onSubmit={handleUpload} className="space-y-4">
            {/* Upload Mode Toggle */}
            <div className="flex gap-4 mb-4">
              <button
                type="button"
                onClick={() => { setUploadMode('generic'); setSelectedUserId(''); setSelectedClient(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  uploadMode === 'generic'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FaUsers />
                Generic Plan (All Users)
              </button>
              <button
                type="button"
                onClick={() => setUploadMode('personalized')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  uploadMode === 'personalized'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FaUser />
                Personalized Plan (Specific Client)
              </button>
            </div>

            {/* Personalized Mode - Client Selection First */}
            {uploadMode === 'personalized' && (
              <div className="space-y-4">
                {/* Client Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Client
                  </label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => handleClientSelect(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">-- Select a client --</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.firstName} {client.lastName} - {client.email}
                      </option>
                    ))}
                  </select>
                  {clients.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No clients with active subscriptions found</p>
                  )}
                </div>

                {/* Client Details Card - Shows when client is selected */}
                {selectedClient && (
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-200 rounded-full flex items-center justify-center">
                        <FaUser className="text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {selectedClient.firstName} {selectedClient.lastName}
                        </h4>
                        <p className="text-sm text-gray-600">{selectedClient.email}</p>
                      </div>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <div className="bg-white px-3 py-1.5 rounded-lg">
                        <span className="text-gray-500">Plan: </span>
                        <span className="font-medium text-primary-700">
                          {getPlanTypeLabel(selectedClient.planType)}
                        </span>
                      </div>
                      <div className="bg-white px-3 py-1.5 rounded-lg">
                        <span className="text-gray-500">Plan ID: </span>
                        <span className="font-medium text-gray-700">{selectedClient.planId}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Form Fields */}
            <div className={`grid grid-cols-1 gap-4 ${uploadMode === 'generic' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
              {/* Plan Type - Only show for generic mode */}
              {uploadMode === 'generic' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Type
                  </label>
                  <select
                    value={planType}
                    onChange={(e) => setPlanType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {PLAN_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Document Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document Type
                </label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* File Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PDF File
                </label>
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
              </div>
            </div>

            {selectedFile && (
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                <FaFilePdf className="text-red-500" />
                <span>{selectedFile.name}</span>
                <span className="text-gray-400">({formatFileSize(selectedFile.size)})</span>
              </div>
            )}

            <button
              type="submit"
              disabled={uploading || !selectedFile || (uploadMode === 'personalized' && !selectedUserId)}
              className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <FaUpload />
                  {uploadMode === 'personalized' && selectedClient
                    ? `Upload for ${selectedClient.firstName}`
                    : 'Upload Document'}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Documents List */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FaFileAlt className="text-primary-600" />
            Uploaded Documents
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="animate-spin text-2xl text-gray-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12 px-4 bg-gray-50 rounded-xl">
              <FaFileAlt className="text-4xl text-gray-300 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-700 mb-2">No Documents Yet</h4>
              <p className="text-gray-500 text-sm">
                Upload meal plans and workout plans for your programs.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Document
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Assigned To
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Plan Type
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Document Type
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Uploaded
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <FaFilePdf className="text-red-500" />
                          <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                            {doc.fileName}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {doc.isPersonalized ? (
                          <div className="flex items-center gap-1">
                            <FaUser className="text-purple-500 text-xs" />
                            <span className="text-sm text-purple-700 font-medium">
                              {doc.assignedToName}
                            </span>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <FaUsers className="text-xs" />
                            All Users
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          {getPlanTypeLabel(doc.planType)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {getDocumentTypeLabel(doc.documentType)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDate(doc.createdAt)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => handleDelete(doc.id)}
                          disabled={deleting === doc.id}
                          className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {deleting === doc.id ? (
                            <FaSpinner className="animate-spin" />
                          ) : (
                            <FaTrash />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Reference */}
        <div className="mt-8 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-6 border border-primary-200">
          <h3 className="font-bold text-gray-900 mb-3">Document Upload Guide</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">
            <div>
              <h4 className="font-semibold text-primary-700 mb-1">Core Program</h4>
              <p>Standard fitness and nutrition plans for regular users.</p>
            </div>
            <div>
              <h4 className="font-semibold text-primary-700 mb-1">Medical Program</h4>
              <p>Specialized plans for users with medical conditions.</p>
            </div>
            <div>
              <h4 className="font-semibold text-primary-700 mb-1">GLP-1 Program</h4>
              <p>Plans designed for users on GLP-1 medications.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
